import React, { useState, useEffect, useCallback } from 'react';
import { GestioneIscrizioniEvento } from './IscrizioniEvento';
import { useConfirmDialog } from './components/ui/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Nomi mesi e giorni in italiano
const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
              'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const GIORNI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const GIORNI_FULL = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

// Colori predefiniti per eventi
const COLORI_EVENTO = [
  { codice: '#3b82f6', nome: 'Blu' },
  { codice: '#22c55e', nome: 'Verde' },
  { codice: '#f59e0b', nome: 'Arancione' },
  { codice: '#ef4444', nome: 'Rosso' },
  { codice: '#8b5cf6', nome: 'Viola' },
  { codice: '#ec4899', nome: 'Rosa' },
  { codice: '#14b8a6', nome: 'Teal' },
  { codice: '#6b7280', nome: 'Grigio' },
];

export function CalendarioClub({ token, onRefresh }) {
  const { showConfirm } = useConfirmDialog();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [eventi, setEventi] = useState([]);
  const [prossimiEventi, setProssimiEventi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  
  // Modal
  const [showNuovoEvento, setShowNuovoEvento] = useState(false);
  const [showDettaglioEvento, setShowDettaglioEvento] = useState(null);
  const [editingEvento, setEditingEvento] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showGestioneIscrizioni, setShowGestioneIscrizioni] = useState(null);
  
  // Form
  const [eventoForm, setEventoForm] = useState({
    titolo: '',
    tipo_evento: '',
    data: '',
    ora_inizio: '',
    ora_fine: '',
    luogo: '',
    descrizione: '',
    note: '',
    colore: '#3b82f6'
  });

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 4000);
  };

  const api = useCallback(async (endpoint, options = {}) => {
    try {
      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers
        }
      });
      return res;
    } catch (err) {
      console.error('API Error:', err);
      return null;
    }
  }, [token]);

  // ============ LOAD DATA ============

  const loadEventi = useCallback(async () => {
    const mese = currentDate.getMonth() + 1;
    const anno = currentDate.getFullYear();
    
    const r = await api(`/api/clubs/calendario/eventi?mese=${mese}&anno=${anno}`);
    if (r?.ok) {
      const data = await r.json();
      const nuoviEventi = data.eventi || [];
      setEventi(nuoviEventi);
      
      // Aggiorna showGestioneIscrizioni se aperta, per evitare reset modale
      setShowGestioneIscrizioni(prev => {
        if (prev) {
          const eventoAggiornato = nuoviEventi.find(e => e.id === prev.id);
          return eventoAggiornato || prev;
        }
        return prev;
      });
      
      // Aggiorna anche showDettaglioEvento se aperta
      setShowDettaglioEvento(prev => {
        if (prev) {
          const eventoAggiornato = nuoviEventi.find(e => e.id === prev.id);
          return eventoAggiornato || prev;
        }
        return prev;
      });
    }
  }, [api, currentDate]);

  const loadProssimiEventi = useCallback(async () => {
    const r = await api('/api/clubs/calendario/prossimi?limit=5');
    if (r?.ok) {
      const data = await r.json();
      setProssimiEventi(data.eventi || []);
    }
  }, [api]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadEventi(), loadProssimiEventi()]);
      setLoading(false);
    };
    loadAll();
  }, [loadEventi, loadProssimiEventi]);

  useEffect(() => {
    loadEventi();
  }, [currentDate, loadEventi]);

  // ============ CALENDAR HELPERS ============

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Giorno della settimana del primo giorno (0=Dom, 1=Lun, ...)
    // Convertiamo in Lun=0, Dom=6
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    return { daysInMonth, startDay };
  };

  const getEventiPerGiorno = (giorno) => {
    const dataStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(giorno).padStart(2, '0')}`;
    return eventi.filter(e => {
      // Backend returns data_inizio, support both for compatibility
      const eventoData = e.data_inizio || e.data || '';
      // Handle both date formats: "2026-03-15" and "2026-03-15T00:00:00"
      return eventoData.startsWith(dataStr);
    });
  };

  const navigateMese = (delta) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // ============ ACTIONS ============

  const resetForm = () => {
    setEventoForm({
      titolo: '',
      tipo_evento: '',
      data: '',
      ora_inizio: '',
      ora_fine: '',
      luogo: '',
      descrizione: '',
      note: '',
      colore: '#3b82f6'
    });
  };

  const openNuovoEvento = (data = null) => {
    resetForm();
    if (data) {
      setEventoForm(prev => ({ ...prev, data }));
    }
    setEditingEvento(null);
    setShowNuovoEvento(true);
  };

  const openEditEvento = (evento) => {
    // Map backend fields to frontend form fields
    setEventoForm({
      titolo: evento.titolo || '',
      tipo_evento: evento.tipo || evento.tipo_evento || '',  // Backend uses "tipo"
      data: evento.data_inizio || evento.data || '',  // Backend uses "data_inizio"
      ora_inizio: evento.ora_inizio || '',
      ora_fine: evento.ora_fine || '',
      luogo: evento.luogo || '',
      descrizione: evento.descrizione || '',
      note: evento.note || '',
      colore: evento.colore || '#3b82f6'
    });
    setEditingEvento(evento);
    setShowNuovoEvento(true);
    setShowDettaglioEvento(null);
  };

  const saveEvento = async () => {
    if (!eventoForm.titolo || !eventoForm.data) {
      showMsg('error', 'Titolo e data sono obbligatori');
      return;
    }

    setSaving(true);
    const url = editingEvento 
      ? `/api/clubs/calendario/eventi/${editingEvento.id}`
      : '/api/clubs/calendario/eventi';
    const method = editingEvento ? 'PUT' : 'POST';
    
    // Map frontend fields to backend expected format
    const payload = {
      titolo: eventoForm.titolo,
      descrizione: eventoForm.descrizione || '',
      data_inizio: eventoForm.data,  // Backend expects "data_inizio"
      data_fine: eventoForm.data,    // Same as start date
      luogo: eventoForm.luogo || '',
      tipo: eventoForm.tipo_evento || 'evento',  // Backend expects "tipo"
      colore: eventoForm.colore || '#3b82f6'
    };
    
    try {
      const r = await api(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (r?.ok) {
        showMsg('success', editingEvento ? 'Evento aggiornato' : 'Evento creato');
        setShowNuovoEvento(false);
        setEditingEvento(null);
        resetForm();
        loadEventi();
        loadProssimiEventi();
        if (onRefresh) onRefresh();
      } else {
        // Handle error response - Pydantic errors are arrays, need to extract message
        try {
          const err = await r?.json();
          let errorMessage = 'Errore salvataggio';
          if (err?.detail) {
            if (typeof err.detail === 'string') {
              errorMessage = err.detail;
            } else if (Array.isArray(err.detail)) {
              // Pydantic validation errors are arrays of objects
              errorMessage = err.detail.map(e => e.msg || e.message || 'Errore validazione').join(', ');
            } else if (typeof err.detail === 'object') {
              errorMessage = err.detail.msg || err.detail.message || JSON.stringify(err.detail);
            }
          }
          showMsg('error', errorMessage);
        } catch {
          showMsg('error', 'Errore salvataggio evento');
        }
      }
    } catch (error) {
      console.error('Save evento error:', error);
      showMsg('error', 'Errore di connessione. Riprova.');
    }
    setSaving(false);
  };

  const deleteEvento = async (id) => {
    showConfirm({
      title: 'Elimina Evento',
      message: 'Sei sicuro di voler eliminare questo evento dal calendario?',
      confirmText: '🗑️ Elimina',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        const r = await api(`/api/clubs/calendario/eventi/${id}`, { method: 'DELETE' });
        if (r?.ok) {
          showMsg('success', 'Evento eliminato');
          setShowDettaglioEvento(null);
          loadEventi();
          loadProssimiEventi();
        }
      }
    });
  };

  const duplicaEvento = async (evento) => {
    const eventoData = evento.data_inizio || evento.data || '';
    const dataDefault = eventoData.split('T')[0]; // Remove time part if present
    const nuovaData = prompt('Inserisci la nuova data (YYYY-MM-DD):', dataDefault);
    if (!nuovaData) return;
    
    const r = await api(`/api/clubs/calendario/duplica/${evento.id}`, {
      method: 'POST',
      body: JSON.stringify({ nuova_data: nuovaData })
    });
    
    if (r?.ok) {
      showMsg('success', 'Evento duplicato');
      loadEventi();
      loadProssimiEventi();
    }
  };

  // ============ HELPERS ============

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      const dayOfWeek = date.getDay();
      const giornoSettimana = GIORNI_FULL[dayOfWeek === 0 ? 6 : dayOfWeek - 1];
      const [year, month, day] = dateStr.split('-');
      return `${giornoSettimana} ${day} ${MESI[parseInt(month) - 1]} ${year}`;
    } catch {
      return dateStr;
    }
  };

  const isToday = (giorno) => {
    const today = new Date();
    return today.getFullYear() === currentDate.getFullYear() &&
           today.getMonth() === currentDate.getMonth() &&
           today.getDate() === giorno;
  };

  const { daysInMonth, startDay } = getDaysInMonth(currentDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          📅 Calendario Club
        </h2>
        <button
          onClick={() => openNuovoEvento()}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          data-testid="btn-nuovo-evento"
        >
          <span>+ Nuovo Evento</span>
        </button>
      </div>

      {/* Messages */}
      {msg.text && (
        <div className={`px-4 py-3 rounded-lg ${msg.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {msg.text}
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Calendario */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow overflow-hidden">
          {/* Header Mese */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex items-center justify-between">
            <button 
              onClick={() => navigateMese(-1)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              ←
            </button>
            <div className="text-center">
              <h3 className="text-xl font-bold">
                {MESI[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h3>
              <button 
                onClick={goToToday}
                className="text-sm text-blue-200 hover:text-white"
              >
                Vai a oggi
              </button>
            </div>
            <button 
              onClick={() => navigateMese(1)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              →
            </button>
          </div>

          {/* Griglia Giorni */}
          <div className="p-4">
            {/* Header giorni settimana */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {GIORNI.map(g => (
                <div key={g} className="text-center text-sm font-medium text-gray-500 py-2">
                  {g}
                </div>
              ))}
            </div>

            {/* Griglia giorni */}
            <div className="grid grid-cols-7 gap-1">
              {/* Giorni vuoti prima del primo giorno */}
              {Array.from({ length: startDay }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square p-1"></div>
              ))}
              
              {/* Giorni del mese */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const giorno = i + 1;
                const eventiGiorno = getEventiPerGiorno(giorno);
                const dataStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(giorno).padStart(2, '0')}`;
                
                return (
                  <div
                    key={giorno}
                    onClick={() => {
                      if (eventiGiorno.length === 1) {
                        setShowDettaglioEvento(eventiGiorno[0]);
                      } else if (eventiGiorno.length > 1) {
                        setSelectedDate(dataStr);
                      } else {
                        openNuovoEvento(dataStr);
                      }
                    }}
                    className={`
                      aspect-square p-1 border rounded-lg cursor-pointer transition-all
                      ${isToday(giorno) ? 'bg-blue-50 border-blue-500' : 'border-gray-100 hover:border-blue-300'}
                      ${eventiGiorno.length > 0 ? 'hover:shadow-md' : 'hover:bg-gray-50'}
                    `}
                  >
                    <div className={`text-sm font-medium mb-1 ${isToday(giorno) ? 'text-blue-600' : 'text-gray-700'}`}>
                      {giorno}
                    </div>
                    <div className="space-y-0.5 overflow-hidden" style={{ maxHeight: '60px' }}>
                      {eventiGiorno.slice(0, 3).map((evento, idx) => (
                        <div
                          key={evento.id}
                          className="text-xs px-1 py-0.5 rounded truncate text-white"
                          style={{ backgroundColor: evento.colore || '#3b82f6' }}
                          title={evento.titolo}
                        >
                          {evento.ora_inizio && <span className="opacity-75">{evento.ora_inizio} </span>}
                          {evento.titolo}
                        </div>
                      ))}
                      {eventiGiorno.length > 3 && (
                        <div className="text-xs text-gray-500 px-1">+{eventiGiorno.length - 3} altri</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sidebar Prossimi Eventi */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow p-4">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              🗓️ Prossimi Eventi
            </h3>
            {prossimiEventi.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">Nessun evento in programma</p>
            ) : (
              <div className="space-y-3">
                {prossimiEventi.map(evento => (
                  <div
                    key={evento.id}
                    onClick={() => setShowDettaglioEvento(evento)}
                    className="p-3 border rounded-lg cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-all"
                  >
                    <div className="flex items-start gap-2">
                      <div 
                        className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                        style={{ backgroundColor: evento.colore || '#3b82f6' }}
                      ></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{evento.titolo}</p>
                        <p className="text-xs text-gray-500">{formatDate(evento.data_inizio || evento.data)}</p>
                        {evento.ora_inizio && (
                          <p className="text-xs text-gray-400">🕐 {evento.ora_inizio}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Legenda Colori */}
          <div className="bg-white rounded-xl shadow p-4">
            <h3 className="font-bold text-gray-800 mb-3 text-sm">🎨 Colori</h3>
            <div className="grid grid-cols-4 gap-2">
              {COLORI_EVENTO.map(c => (
                <div
                  key={c.codice}
                  className="w-6 h-6 rounded-full cursor-pointer hover:scale-110 transition-transform"
                  style={{ backgroundColor: c.codice }}
                  title={c.nome}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ============ MODALS ============ */}

      {/* Modal Nuovo/Modifica Evento */}
      {showNuovoEvento && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold">
                {editingEvento ? '✏️ Modifica Evento' : '📅 Nuovo Evento'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Titolo */}
              <div>
                <label className="block text-sm font-medium mb-1">Titolo *</label>
                <input
                  type="text"
                  value={eventoForm.titolo}
                  onChange={(e) => setEventoForm({ ...eventoForm, titolo: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Es: Torneo Primavera, Allenamento settimanale..."
                  data-testid="evento-titolo"
                />
              </div>

              {/* Tipo Evento */}
              <div>
                <label className="block text-sm font-medium mb-1">Tipo Evento</label>
                <input
                  type="text"
                  value={eventoForm.tipo_evento}
                  onChange={(e) => setEventoForm({ ...eventoForm, tipo_evento: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Es: Torneo, Allenamento, Campionato, Riunione..."
                  data-testid="evento-tipo"
                />
              </div>

              {/* Data */}
              <div>
                <label className="block text-sm font-medium mb-1">Data *</label>
                <input
                  type="date"
                  value={eventoForm.data}
                  onChange={(e) => setEventoForm({ ...eventoForm, data: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="evento-data"
                />
              </div>

              {/* Orari */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Ora Inizio</label>
                  <input
                    type="time"
                    value={eventoForm.ora_inizio}
                    onChange={(e) => setEventoForm({ ...eventoForm, ora_inizio: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Ora Fine</label>
                  <input
                    type="time"
                    value={eventoForm.ora_fine}
                    onChange={(e) => setEventoForm({ ...eventoForm, ora_fine: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Luogo */}
              <div>
                <label className="block text-sm font-medium mb-1">Luogo</label>
                <input
                  type="text"
                  value={eventoForm.luogo}
                  onChange={(e) => setEventoForm({ ...eventoForm, luogo: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Es: Sede club, Palestra comunale..."
                />
              </div>

              {/* Descrizione */}
              <div>
                <label className="block text-sm font-medium mb-1">Descrizione</label>
                <textarea
                  value={eventoForm.descrizione}
                  onChange={(e) => setEventoForm({ ...eventoForm, descrizione: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg h-20 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Descrizione dell'evento..."
                />
              </div>

              {/* Colore */}
              <div>
                <label className="block text-sm font-medium mb-2">Colore</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORI_EVENTO.map(c => (
                    <button
                      key={c.codice}
                      type="button"
                      onClick={() => setEventoForm({ ...eventoForm, colore: c.codice })}
                      className={`w-8 h-8 rounded-full transition-all ${
                        eventoForm.colore === c.codice ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: c.codice }}
                      title={c.nome}
                    />
                  ))}
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium mb-1">Note</label>
                <textarea
                  value={eventoForm.note}
                  onChange={(e) => setEventoForm({ ...eventoForm, note: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg h-16 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Note aggiuntive..."
                />
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowNuovoEvento(false); setEditingEvento(null); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={saveEvento}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                data-testid="evento-submit"
              >
                {saving ? 'Salvataggio...' : (editingEvento ? 'Aggiorna' : 'Crea Evento')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dettaglio Evento */}
      {showDettaglioEvento && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div 
              className="p-6 text-white rounded-t-xl"
              style={{ backgroundColor: showDettaglioEvento.colore || '#3b82f6' }}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm opacity-80">{showDettaglioEvento.tipo || showDettaglioEvento.tipo_evento || 'Evento'}</p>
                  <h3 className="text-xl font-bold">{showDettaglioEvento.titolo}</h3>
                </div>
                <button
                  onClick={() => setShowDettaglioEvento(null)}
                  className="p-1 hover:bg-white/20 rounded"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 text-gray-700">
                <span className="text-xl">📅</span>
                <span>{formatDateFull(showDettaglioEvento.data_inizio || showDettaglioEvento.data)}</span>
              </div>
              
              {(showDettaglioEvento.ora_inizio || showDettaglioEvento.ora_fine) && (
                <div className="flex items-center gap-3 text-gray-700">
                  <span className="text-xl">🕐</span>
                  <span>
                    {showDettaglioEvento.ora_inizio || '--:--'}
                    {showDettaglioEvento.ora_fine && ` - ${showDettaglioEvento.ora_fine}`}
                  </span>
                </div>
              )}
              
              {showDettaglioEvento.luogo && (
                <div className="flex items-center gap-3 text-gray-700">
                  <span className="text-xl">📍</span>
                  <span>{showDettaglioEvento.luogo}</span>
                </div>
              )}
              
              {showDettaglioEvento.descrizione && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{showDettaglioEvento.descrizione}</p>
                </div>
              )}
              
              {showDettaglioEvento.note && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500">{showDettaglioEvento.note}</p>
                </div>
              )}

              {/* Status Iscrizioni */}
              {showDettaglioEvento.iscrizioni_abilitate && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700 font-medium">✅ Iscrizioni abilitate</p>
                </div>
              )}
            </div>
            <div className="p-6 border-t space-y-3">
              {/* Pulsante Gestione Iscrizioni */}
              <button
                onClick={() => {
                  setShowGestioneIscrizioni(showDettaglioEvento);
                  setShowDettaglioEvento(null);
                }}
                className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center justify-center gap-2"
                data-testid="btn-gestione-iscrizioni"
              >
                📝 Gestione Iscrizioni
              </button>
              
              <div className="flex justify-between">
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditEvento(showDettaglioEvento)}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                  >
                    ✏️ Modifica
                  </button>
                  <button
                    onClick={() => duplicaEvento(showDettaglioEvento)}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    📋 Duplica
                  </button>
                </div>
                <button
                  onClick={() => deleteEvento(showDettaglioEvento.id)}
                  className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                >
                  🗑️ Elimina
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Lista Eventi per Data */}
      {selectedDate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">📅 Eventi del {formatDate(selectedDate)}</h3>
              <button
                onClick={() => setSelectedDate(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
              {eventi
                .filter(e => {
                  const eventoData = e.data_inizio || e.data || '';
                  return eventoData.startsWith(selectedDate);
                })
                .map(evento => (
                  <div
                    key={evento.id}
                    onClick={() => { setSelectedDate(null); setShowDettaglioEvento(evento); }}
                    className="p-4 border rounded-lg cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: evento.colore || '#3b82f6' }}
                      ></div>
                      <div className="flex-1">
                        <p className="font-medium">{evento.titolo}</p>
                        <p className="text-sm text-gray-500">
                          {(evento.tipo || evento.tipo_evento) && <span>{evento.tipo || evento.tipo_evento} • </span>}
                          {evento.ora_inizio && <span>🕐 {evento.ora_inizio}</span>}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
            <div className="p-6 border-t">
              <button
                onClick={() => { setSelectedDate(null); openNuovoEvento(selectedDate); }}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                + Aggiungi Evento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestione Iscrizioni */}
      {showGestioneIscrizioni && (
        <GestioneIscrizioniEvento
          evento={showGestioneIscrizioni}
          token={token}
          onUpdate={() => { loadEventi(); loadProssimiEventi(); }}
          onClose={() => setShowGestioneIscrizioni(null)}
        />
      )}
    </div>
  );
}

export default CalendarioClub;
